import { ReactElement, useMemo } from 'react';
import { TableInstance } from 'react-table';
import { Select, SelectProps } from '../../../Form';
import { TableRowDataType } from '../../types';

export type OptionValue = string | number;
export type Option = { label: string; value: OptionValue };

export type MultiSelectFilterProps<TableRowData extends TableRowDataType> = SelectProps<Option, true> & {
  tableInstance: TableInstance<TableRowData>;
  columnId: string;
  onChange?: (selectedOptions: OptionValue[]) => void;
};

export function MultiSelectFilter<TableRowData extends TableRowDataType>({
  tableInstance,
  columnId,
  onChange,
  ...selectProps
}: MultiSelectFilterProps<TableRowData>): ReactElement {
  const options: Option[] = useMemo(() => {
    if (tableInstance) {
      const { preFilteredRows } = tableInstance;

      const optionValues = preFilteredRows.reduce((acc, { values }) => {
        const value = values[columnId];
        return acc.includes(value) ? acc : [...acc, value];
      }, new Array<OptionValue>());

      return optionValues.sort().map((value) => ({
        value,
        label: typeof value === 'number' ? value.toString() : value,
      }));
    }
    return [];
  }, [tableInstance, columnId]);

  const handleChange = (values: readonly Option[]) => {
    const { setFilter } = tableInstance;
    const selectedValues = values.map(({ value }) => value);
    setFilter(columnId, selectedValues);
    if (onChange) {
      onChange(selectedValues);
    }
  };

  return (
    <Select<Option, true>
      {...selectProps}
      placeholder={tableInstance ? undefined : 'Loading filter...'}
      options={options}
      onChange={handleChange}
      isDisabled={!tableInstance}
      isMulti
    />
  );
}
